import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";

export interface PromotionCategory {
  slug: string;
  discount_percent: number;
  label: string;
}

export interface Promotion {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  starts_at: string;
  ends_at: string;
  discount_percent: number;
  hero_title: string | null;
  hero_subtitle: string | null;
  announcement_text: string | null;
  coupon_code: string | null;
  categories: PromotionCategory[];
  config: Record<string, unknown>;
}

async function fetchActivePromotion(): Promise<Promotion | null> {
  const now = new Date().toISOString();
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from("promotions")
    .select("*")
    .eq("status", "active")
    .lte("starts_at", now)
    .gte("ends_at", now)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  return {
    ...data,
    categories: (data.categories as unknown as PromotionCategory[]) || [],
    config: (data.config as unknown as Record<string, unknown>) || {},
  };
}

export function useActivePromotion() {
  return useQuery({
    queryKey: ["active-promotion"],
    queryFn: fetchActivePromotion,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Get discount percent for a specific category from the promotion,
 * falling back to the general discount_percent.
 */
export function getCategoryDiscount(
  promotion: Promotion | null | undefined,
  categorySlug?: string
): number {
  if (!promotion) return 30; // fallback
  if (categorySlug) {
    const cat = promotion.categories.find((c) => c.slug === categorySlug);
    if (cat) return cat.discount_percent;
  }
  return promotion.discount_percent;
}
